// PriceAlertNotifications — the one watcher of the price alerts, mounted once
// in App beside the limit orders' notifications, so it runs on every page of
// the site for as long as a tab of it is open (and only then).
//
// It listens to the shared live prices (lib/livePriceStore: the platform's
// trade tape, the open token page, the portfolio; shared across this site's
// tabs). When a price carries an armed alert across its line, the alert is
// marked fired under a Web Lock — two open tabs see the same price, one of
// them fires — and then said: the product's toast (a press opens the token)
// and, when the user switched it on and the browser allows it, a browser
// notification. The header's Notifications list is not told: it lists the
// wallet tracker's notices from the server and takes no local item.

import { BellRingingIcon } from '@phosphor-icons/react'
import { useEffect, useRef } from 'react'
import { useOptionalPixelToast } from '../components/ui/pixel-toast'
import { getLivePrice, subscribeLivePrices } from './livePriceStore'
import { pairRouteNavigationState, primaryPairPath } from './pairRoutes'
import {
  type PriceAlert,
  alertFiredText,
  dueAlerts,
  formatAlertValue,
  markAlertFired,
  readPriceAlerts,
} from './priceAlerts'
import { canonicalTonAddress } from './tonAddress'

type Toast = ReturnType<typeof useOptionalPixelToast>

const LOCK = 'wils-price-alerts'
/** How long a fired alert's toast stays: longer than a copy's three seconds. */
export const ALERT_TOAST_MS = 12_000

/**
 * Opens a page of the app from outside the router (the watcher stands above
 * it): the router's own history entry, then the event its listener follows.
 */
export function openInApp(path: string, state?: unknown): void {
  const idx = ((window.history.state as { idx?: number } | null)?.idx ?? 0) + 1
  const entry = { usr: state ?? null, key: Math.random().toString(36).slice(2, 10), idx }
  window.history.pushState(entry, '', path)
  window.dispatchEvent(new PopStateEvent('popstate', { state: entry }))
}

function tokenPath(a: PriceAlert): { path: string; state: unknown } {
  return { path: primaryPairPath(a.master), state: pairRouteNavigationState(a.pool) }
}

function announce(a: PriceAlert, value: number, toast: Toast): void {
  const said = alertFiredText(a)
  const now = formatAlertValue(a.metric, a.unit, value)
  const open = () => {
    const { path, state } = tokenPath(a)
    openInApp(path, state)
  }
  toast?.push({
    tone: 'info',
    icon: <BellRingingIcon size={15} weight="fill" color="var(--soft-gold)" />,
    content: `${a.ticker || 'Token'} ${said}`,
    tail: `now ${now}`,
    close: true,
    duration: ALERT_TOAST_MS,
    onClick: open,
  })
  if (!readPriceAlerts().browser) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  try {
    const n = new Notification(`${a.ticker || 'Token'} — price alert`, {
      body: `${said[0]?.toUpperCase()}${said.slice(1)} — now ${now}`,
      tag: a.id,
    })
    n.onclick = () => {
      window.focus()
      open()
      n.close()
    }
  } catch {
    // a browser that will not make one here: the toast has said it
  }
}

/** Fires one alert: marked under the lock (once across tabs), then said. */
export function fireAlert(a: PriceAlert, value: number, toast: Toast): Promise<void> | void {
  const run = () => {
    if (markAlertFired(a.id, value)) announce(a, value, toast)
  }
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (locks?.request) return locks.request(LOCK, run).then(() => undefined)
  run()
}

/** The tokens with an armed alert, by the store's own key and canonical. */
function armedTokens(): Set<string> {
  const out = new Set<string>()
  for (const a of readPriceAlerts().alerts) if (a.firedAt === undefined) out.add(a.master)
  return out
}

function canonical(token: string): string {
  try {
    return canonicalTonAddress(token) ?? token
  } catch {
    return token
  }
}

export function PriceAlertNotifications() {
  const toast = useOptionalPixelToast()
  const toastRef = useRef(toast)
  toastRef.current = toast
  useEffect(
    () =>
      subscribeLivePrices((token) => {
        const armed = armedTokens()
        if (armed.size === 0) return
        const key = armed.has(token) ? token : canonical(token)
        if (!armed.has(key)) return
        const entry = getLivePrice(token)
        if (!entry) return
        for (const { alert, value } of dueAlerts(readPriceAlerts().alerts, key, entry)) {
          void fireAlert(alert, value, toastRef.current)
        }
      }),
    []
  )
  return null
}
